import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { MessageSquare, AlertCircle, AlertTriangle, Clock, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

interface SupportStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  byPriority: {
    urgent: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface SupportRequestsWidgetProps {
  stats: SupportStats;
  lastUpdated: Date | null;
}

export function SupportRequestsWidget({ stats, lastUpdated }: SupportRequestsWidgetProps) {
  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Support Requests
            </CardTitle>
            <CardDescription className="flex items-center gap-2">
              Pending user support tickets
              {lastUpdated && (
                <span className="inline-flex items-center gap-1 text-xs text-green-600">
                  <RefreshCw className="h-3 w-3" />
                  Live
                </span>
              )}
            </CardDescription>
          </div>
          <Badge variant={stats.open > 0 ? "destructive" : "secondary"}>
            {stats.open + stats.inProgress} pending
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 flex-1 overflow-auto">
        {/* Status Breakdown */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Open</span>
            <span className="font-medium">{stats.open}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">In Progress</span>
            <span className="font-medium">{stats.inProgress}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Resolved</span>
            <span className="font-medium text-green-600">{stats.resolved}</span>
          </div>
        </div>

        {/* Priority Breakdown */}
        <div className="pt-3 border-t">
          <p className="text-sm font-medium mb-3">Priority Breakdown</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm flex-1">Urgent</span>
              <Badge variant="destructive" className="min-w-[2rem] justify-center">
                {stats.byPriority.urgent}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <span className="text-sm flex-1">High</span>
              <Badge className="bg-orange-500 hover:bg-orange-600 min-w-[2rem] justify-center">
                {stats.byPriority.high}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span className="text-sm flex-1">Medium</span>
              <Badge className="bg-yellow-500 hover:bg-yellow-600 min-w-[2rem] justify-center">
                {stats.byPriority.medium}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span className="text-sm flex-1">Low</span>
              <Badge className="bg-blue-500 hover:bg-blue-600 min-w-[2rem] justify-center">
                {stats.byPriority.low}
              </Badge>
            </div>
          </div>
        </div>

        {/* Resolution Rate */}
        {stats.total > 0 && (
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Resolution Rate</span>
              <span className="font-medium">
                {Math.round((stats.resolved / stats.total) * 100)}%
              </span>
            </div>
            <Progress 
              value={(stats.resolved / stats.total) * 100} 
              className="h-2"
            />
          </div>
        )}

        <Link to="/admin/messages">
          <Button variant="outline" className="w-full mt-2">
            <MessageSquare className="h-4 w-4 mr-2" />
            View All Messages
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
